import { useCallback, useEffect, useState } from 'react';
import {
  ActivityIndicator,
  Alert,
  Modal,
  Platform,
  Pressable,
  ScrollView,
  View,
} from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { useRouter } from 'expo-router';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { useTheme } from '@/theme';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Body, Caption, SubHeading } from '@/components/ui/Text';
import { usePremiumUpsellStore } from '@/state/premiumUpsellStore';
import { useUserStore } from '@/state/userStore';
import { useOfflinePlanTierDisplay } from '@/hooks/useOfflinePlanTierDisplay';
import { logAnalyticsEvent } from '@/services/analyticsLogger';
import { refreshEntitlementState } from '@/services/billing';
import { getRevenueCatInitStatus, restoreRevenueCatPurchases } from '@/services/revenuecat';
import { addBreadcrumb } from '@/services/errorTracking';

const TESTIMONIAL = {
  quote: 'Tripvio Pro gives me everything I need to plan and manage multiple trips.',
  author: 'Sarah M.',
};

const FEATURES: Array<{
  id: string;
  icon: keyof typeof Ionicons.glyphMap;
  iconColor: string;
  title: string;
  description: string;
}> = [
  {
    id: 'planning',
    icon: 'calendar-outline',
    iconColor: '#4a90d9',
    title: 'Unlimited planning',
    description: 'Plan as many trips as you like. No limits on itineraries or destinations.',
  },
  {
    id: 'multi-trip',
    icon: 'layers-outline',
    iconColor: '#5cb85c',
    title: 'Multiple trips at once',
    description: 'Plan several trips simultaneously. Perfect for frequent travelers.',
  },
  {
    id: 'ai',
    icon: 'sparkles-outline',
    iconColor: '#9b59b6',
    title: 'Unlimited AI recommendations',
    description: 'Get AI-powered travel suggestions anytime. No monthly cap.',
  },
  {
    id: 'offline',
    icon: 'phone-portrait-outline',
    iconColor: '#4a90d9',
    title: 'Offline mode',
    description:
      'Access itinerary, documents, and map pins offline. (Safety offline is also available on free.)',
  },
  {
    id: 'flight',
    icon: 'airplane-outline',
    iconColor: '#f0ad4e',
    title: 'Live flight updates',
    description: 'Monitor flight status and get notified of changes.',
  },
  {
    id: 'attachments',
    icon: 'document-attach-outline',
    iconColor: '#d9534f',
    title: 'Unlimited attachments',
    description: 'Store all trip files and PDFs in one place.',
  },
];

export function PremiumUpsellModal() {
  const theme = useTheme();
  const router = useRouter();
  const insets = useSafeAreaInsets();
  const setProfileDetails = useUserStore((s) => s.setProfileDetails);
  const firebaseUser = useUserStore((s) => s.firebaseUser);
  const profile = useUserStore((s) => s.profile);
  const visible = usePremiumUpsellStore((s) => s.visible);
  const source = usePremiumUpsellStore((s) => s.source);
  const close = usePremiumUpsellStore((s) => s.close);
  const [restoring, setRestoring] = useState(false);
  const offlinePlan = useOfflinePlanTierDisplay(firebaseUser?.uid, {
    isPremium: profile?.isPremium,
    planTier: profile?.planTier,
    entitlement: profile?.entitlement,
  });

  useEffect(() => {
    if (!visible || !offlinePlan.effectivePremium) return;
    close();
  }, [visible, offlinePlan.effectivePremium, close]);

  useEffect(() => {
    if (!visible) return;
    addBreadcrumb('PremiumUpsell modal visible', 'ui.overlay', 'info', {
      source: source ?? 'unknown',
    });
  }, [visible, source]);

  const handleUpgrade = useCallback(() => {
    void logAnalyticsEvent('premium_paywall_started', {
      source: 'marketing_upsell',
      plan: 'premium',
    });
    close();
    router.replace('/(tabs)/profile?openUpgrade=1');
  }, [close, router]);

  const handleRestore = useCallback(async () => {
    if (Platform.OS === 'web') {
      Alert.alert('Restore purchases', 'Use Profile → Restore / Refresh purchase status on this device.');
      return;
    }
    setRestoring(true);
    try {
      const sleep = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));
      const isSnapshotPremium = (snapshot: Awaited<ReturnType<typeof refreshEntitlementState>>) => {
        const status = snapshot.subscriptionStatus;
        return (
          snapshot.isPremium ||
          snapshot.planTier === 'premium' ||
          status === 'active' ||
          status === 'grace_period'
        );
      };
      const restoreAttempted = await restoreRevenueCatPurchases();
      if (!restoreAttempted) {
        const rcStatus = getRevenueCatInitStatus().status;
        if (rcStatus === 'disabled') {
          Alert.alert(
            'Restore unavailable in this build',
            'In-app billing is disabled for this environment. Use the production build or refresh status on Profile.',
          );
          return;
        }
        if (rcStatus === 'missing_key') {
          Alert.alert(
            'Restore unavailable',
            'Billing setup is incomplete for this app build. Please contact support.',
          );
          return;
        }
      }
      let snapshot = await refreshEntitlementState();
      if (restoreAttempted && !isSnapshotPremium(snapshot)) {
        for (const delayMs of [1200, 2500]) {
          await sleep(delayMs);
          snapshot = await refreshEntitlementState();
          if (isSnapshotPremium(snapshot)) break;
        }
      }
      const normalizedPremium = isSnapshotPremium(snapshot);
      setProfileDetails({
        isPremium: normalizedPremium,
        planTier: snapshot.planTier,
        entitlement: {
          tier: normalizedPremium ? 'premium' : 'free',
          status:
            snapshot.subscriptionStatus === 'active' ||
            snapshot.subscriptionStatus === 'grace_period' ||
            snapshot.subscriptionStatus === 'cancelled' ||
            snapshot.subscriptionStatus === 'expired'
              ? snapshot.subscriptionStatus
              : 'inactive',
          provider:
            snapshot.entitlementProvider === 'stripe' ||
            snapshot.entitlementProvider === 'revenuecat' ||
            snapshot.entitlementProvider === 'unknown'
              ? snapshot.entitlementProvider
              : 'manual',
          expiresAt: snapshot.entitlementExpiresAt ?? undefined,
        },
      });
      Alert.alert(
        'Account updated',
        restoreAttempted
          ? 'Your purchase restore and subscription status refresh completed.'
          : 'Your subscription status has been refreshed.',
      );
      if (normalizedPremium) close();
    } catch (error) {
      const message =
        error instanceof Error && error.message
          ? error.message
          : 'Could not refresh subscription status right now. Please try again.';
      Alert.alert('Unable to restore', message);
    } finally {
      setRestoring(false);
    }
  }, [close, setProfileDetails]);

  if (!visible) {
    return null;
  }

  return (
    <Modal
      visible
      animationType="slide"
      presentationStyle={Platform.OS === 'ios' ? 'fullScreen' : undefined}
      onRequestClose={close}
    >
      <View
        style={{
          flex: 1,
          backgroundColor: theme.colors.backgroundSecondary,
          paddingTop: insets.top,
          paddingBottom: Math.max(insets.bottom, theme.spacing(2)),
        }}
      >
        <View
          style={{
            flexDirection: 'row',
            alignItems: 'center',
            justifyContent: 'space-between',
            paddingHorizontal: theme.spacing(2),
            paddingVertical: theme.spacing(1.5),
          }}
        >
          <Pressable
            onPress={close}
            accessibilityRole="button"
            accessibilityLabel="Close"
            style={{
              width: 40,
              height: 40,
              borderRadius: 20,
              backgroundColor: theme.colors.surface,
              alignItems: 'center',
              justifyContent: 'center',
              borderWidth: 1,
              borderColor: theme.colors.borderMuted,
            }}
          >
            <Ionicons name="close" size={22} color={theme.colors.text} />
          </Pressable>
          <View style={{ flexDirection: 'row', alignItems: 'center', gap: theme.spacing(0.75) }}>
            <Ionicons name="airplane" size={18} color={theme.colors.success} />
            <Caption
              style={{
                fontWeight: '700',
                letterSpacing: 0.5,
                color: theme.colors.text,
                backgroundColor: theme.colors.primaryContainer,
                paddingHorizontal: theme.spacing(1),
                paddingVertical: 4,
                borderRadius: theme.radii.sm,
              }}
            >
              PRO
            </Caption>
          </View>
        </View>

        <ScrollView
          style={{ flex: 1 }}
          contentContainerStyle={{
            paddingHorizontal: theme.spacing(2),
            paddingBottom: theme.spacing(3),
          }}
          keyboardShouldPersistTaps="handled"
        >
          <Card variant="outlined" padding={theme.spacing(2)} style={{ marginBottom: theme.spacing(2) }}>
            <Body style={{ fontStyle: 'italic', lineHeight: 22 }}>&ldquo;{TESTIMONIAL.quote}&rdquo;</Body>
            <Caption color={theme.colors.textSecondary} style={{ marginTop: theme.spacing(1) }}>
              — {TESTIMONIAL.author}
            </Caption>
          </Card>

          <Card variant="outlined" padding={theme.spacing(2)}>
            <SubHeading style={{ marginBottom: theme.spacing(1.5) }}>Tripvio Pro</SubHeading>
            {FEATURES.map((f) => (
              <View
                key={f.id}
                style={{
                  flexDirection: 'row',
                  gap: theme.spacing(1.5),
                  paddingVertical: theme.spacing(1.25),
                  borderBottomWidth: f.id === FEATURES[FEATURES.length - 1].id ? 0 : 1,
                  borderBottomColor: theme.colors.borderMuted,
                }}
              >
                <View
                  style={{
                    width: 40,
                    height: 40,
                    borderRadius: theme.radii.md,
                    backgroundColor: `${f.iconColor}22`,
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <Ionicons name={f.icon} size={22} color={f.iconColor} />
                </View>
                <View style={{ flex: 1 }}>
                  <Body style={{ fontWeight: '600', marginBottom: 4 }}>{f.title}</Body>
                  <Caption color={theme.colors.textSecondary} style={{ lineHeight: 18 }}>
                    {f.description}
                  </Caption>
                </View>
              </View>
            ))}
          </Card>
        </ScrollView>

        <View style={{ paddingHorizontal: theme.spacing(2), gap: theme.spacing(1.5) }}>
          <Button label="Upgrade" onPress={handleUpgrade} />
          <Pressable
            onPress={handleRestore}
            disabled={restoring}
            accessibilityRole="button"
            accessibilityLabel="Restore purchases"
            style={{ alignItems: 'center', paddingVertical: theme.spacing(1) }}
          >
            {restoring ? (
              <ActivityIndicator color={theme.colors.primary} />
            ) : (
              <Caption style={{ color: theme.colors.primary, fontWeight: '600' }}>Restore purchases</Caption>
            )}
          </Pressable>
        </View>
      </View>
    </Modal>
  );
}
